'use client';

import React, { useRef } from 'react';

import { cn } from '@/lib/utils';
import { PortableText } from '@portabletext/react';
import { richTextComponent } from '@/sanity/sanityPortableText';
import {
  useReducedMotion,
  useTransform,
  type MotionValue,
} from 'framer-motion';

import Chip from '@/components/global/ui/Chip';
import DecorationLine from '@/components/global/ui/DecorationLine';
import { CTACornerButton } from '@/shadcn/ui/corner-button';
import { MotionDiv, MotionP } from '@/components/global/utils/motionTags';
import {
  REVEAL_EASE,
  SPAN_OFFSET,
  useRevealProgress,
} from '@/components/global/utils/scrollReveal';

import { PageCareerSchema } from '@/sanity/pages/PageCareerTemplate';

type Step = PageCareerSchema['processItems'][number];

/** Connector track, and the dot centred on it — the design offsets the dot 3px left. */
const TRACK_X = '9rem';
const DOT_X = '8.625rem';

/**
 * Fraction of a step's own slice that its light-up occupies.
 *
 * Deliberately short. `useReveal` is the shared helper for this, but it is built
 * for stacked cascades: REVEAL_WINDOW is 0.45 against starts spread over 0.55, so
 * four or five siblings are always mid-reveal — which is the point when a group
 * should read as one gesture. Here the steps are not a group, they are a progress
 * readout, and each one has to light as the bar reaches its dot. A wide window
 * would leave the step you are reading still half dim while the bar sat on it.
 */
const LIGHT_WINDOW = 0.6;

function ProcessStep({
  item,
  index,
  total,
  progress,
  reduceMotion,
}: {
  item: Step;
  index: number;
  total: number;
  progress: MotionValue<number>;
  reduceMotion: boolean;
}) {
  /*
    The fill covers the list top-down in step with `progress`, and the steps are
    close to evenly spaced down it, so index/total is where the bar meets this
    step's dot. Every part of the step then hangs off the one reveal, so the tab
    lighting up, the dot turning green and the copy coming out of its dimmed state
    happen as a single move rather than three.
  */
  const start = index / total;
  const reveal = useTransform(
    progress,
    [start, start + LIGHT_WINDOW / total],
    reduceMotion ? [1, 1] : [0, 1],
    { ease: REVEAL_EASE },
  );
  const opacity = useTransform(reveal, [0, 1], [0.4, 1]);
  const tabBackground = useTransform(
    reveal,
    [0, 1],
    ['rgba(255,255,255,0.1)', '#D9F9B9'],
  );
  const numberColor = useTransform(reveal, [0, 1], ['#FFFFFF', '#0A1A1D']);
  const dotBackground = useTransform(
    reveal,
    [0, 1],
    ['rgba(255,255,255,0.15)', '#D9F9B9'],
  );

  return (
    <MotionDiv className="flex items-stretch gap-[2rem]" style={{ opacity }}>
      <div className="relative w-[9.25rem] shrink-0">
        {/* the tab's bottom-left corner is cut, as drawn */}
        <MotionDiv
          className="flex w-[6.625rem] items-center justify-center px-[2rem] py-[1rem] [clip-path:polygon(0_0,100%_0,100%_100%,1.82rem_100%,0_calc(100%-1.82rem))]"
          style={{ backgroundColor: tabBackground }}
        >
          <MotionP
            className="mb-0 font-geist text-[2.25rem] font-medium leading-[1.5]"
            style={{ color: numberColor }}
          >
            {index + 1}
          </MotionP>
        </MotionDiv>

        <MotionDiv
          className="absolute top-0 h-[0.625rem] w-[0.625rem]"
          style={{ left: DOT_X, backgroundColor: dotBackground }}
        />
      </div>

      <div className="flex-1 bg-white/[.05] p-[3rem]">
        {item?.title && (
          <h3 className="mb-0 font-geist !text-[2.5rem] font-medium leading-[1.5] text-white sm:!text-[2.25rem]">
            {item.title}
          </h3>
        )}

        {item?.text && (
          <p className="mb-0 font-geist text-[2.25rem] leading-[1.5] text-white/60">
            {item.text}
          </p>
        )}
      </div>
    </MotionDiv>
  );
}

export default function ProcessSection({
  data,
  className,
}: {
  data: PageCareerSchema;
  className?: string;
}) {
  const items = data?.processItems;
  const stepsRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion() ?? false;

  /*
    SPAN_OFFSET spreads the range over the list's own height, so the fill tracks
    where you actually are in the process rather than racing ahead of it.
  */
  const progress = useRevealProgress(stepsRef, SPAN_OFFSET);
  const fillHeight = useTransform(progress, [0, 1], ['0%', '100%']);

  if (!items?.length) return null;

  return (
    <section
      className={cn(
        'relative overflow-clip bg-[#0A1A1D] pb-[7rem] pt-[9rem] sm:pb-[10.625rem] sm:pt-[15rem]',
        className,
      )}
    >
      {/* the section's own rails, spanning its full height */}
      <div className="pointer-events-none absolute inset-0 mx-auto hidden w-[min(178.75rem,100%)] lg:block">
        <DecorationLine
          className="left-0 top-0"
          orientation="vertical"
          style="light"
        />
        <DecorationLine
          className="right-0 top-0"
          orientation="vertical"
          style="light"
        />
      </div>

      <div className="relative mx-auto flex w-[min(178.75rem,100%)] flex-col items-center px-[2rem] sm:px-[3rem] md:px-[5rem] lg:px-0">
        {/* header */}
        <div className="flex w-[min(52.5rem,100%)] flex-col items-center gap-[1rem] sm:gap-[1.75rem]">
          {data?.processChip?.text && (
            <Chip
              className="mb-0 [&_p]:uppercase"
              image={data?.processChip?.icon}
              text={data?.processChip?.text}
              textBG="bg-[#F3F2F1]"
            />
          )}

          {data?.processTitle && (
            /* the green run is the "Green background underline" decorator, which carries its own colour and serif */
            <div className="richtext richtext-center prose-headings:text-white">
              <PortableText
                value={data.processTitle}
                components={richTextComponent}
              />
            </div>
          )}
        </div>

        {/* the rules bracketing the steps are dropped on mobile, as drawn */}
        <div className="relative mt-[5.25rem] hidden h-[2px] w-full sm:block">
          <DecorationLine
            className="top-0"
            orientation="horizontal"
            style="light"
          />
        </div>

        {/* steps */}
        <div
          ref={stepsRef}
          className="relative mt-[2.5rem] w-[min(96.5rem,100%)] sm:mt-[5.5rem]"
        >
          {/* track, and the fill that grows down it as the list scrolls past */}
          <div
            className="absolute top-0 hidden h-full w-px bg-white/[.15] lg:block"
            style={{ left: TRACK_X }}
          />
          <MotionDiv
            className="absolute top-0 hidden w-px bg-[#D9F9B9] lg:block"
            style={{ left: TRACK_X, height: fillHeight }}
          />

          <div className="flex flex-col gap-[2rem]">
            {items.map((item, index) => (
              <ProcessStep
                key={index}
                item={item}
                index={index}
                total={items.length}
                progress={progress}
                reduceMotion={reduceMotion}
              />
            ))}
          </div>
        </div>

        {/* the rules bracketing the steps are dropped on mobile, as drawn */}
        <div className="relative mt-[5.25rem] hidden h-[2px] w-full sm:block">
          <DecorationLine
            className="top-0"
            orientation="horizontal"
            style="light"
          />
        </div>

        {data?.processCTA && (
          <CTACornerButton
            className="mt-[3.25rem] sm:mt-[7.5rem]"
            data={data.processCTA}
          />
        )}
      </div>
    </section>
  );
}
